import {AuthApiClient} from "./AuthApiClient";
import {CheckProviderRequest, CheckProviderResponse, SigninRequest, SessionDataResponse, UserInfo} from "./dtos";

type MockUser = {
    password: string | null;
    provider: string | null;
};

export class MockAuthApiClient implements AuthApiClient {
    private mockUsers = new Map<string, MockUser>();
    private mockSession: SessionDataResponse | null = null;

    constructor() {
        // Domyślni użytkownicy do testów
        this.mockUsers.set('user@example.com', { password: 'password123', provider: null });
        this.mockUsers.set('tomasz.krzyzak@rhizomind.com', { password: null, provider: 'rhizomind' });
        this.mockUsers.set('tomasz.krzyzak@solidify.pl', { password: null, provider: 'solidify' });
    }

    async checkProvider(request: CheckProviderRequest): Promise<CheckProviderResponse> {
        await this.simulateDelay();
        const user = this.mockUsers.get(request.email);
        return { provider: user?.provider ?? null };
    }

    async signin(request: SigninRequest): Promise<void> {
        await this.simulateDelay();
        const user = this.mockUsers.get(request.email);

        if (!user || user.password === null || user.password !== request.password) {
            throw new Error('Invalid credentials');
        }

        // Ustawienie symulowanej sesji
        this.mockSession = {
            sessionId: `mock-session-${Date.now()}`,
            expiresAt: new Date(Date.now() + 3600000).toISOString(), // 1h
            userInfo: {
                id: 'mock-user-id',
                email: request.email,
                name: 'Mock User',
            },
        };
    }

    initiateOAuthSignin(provider: string): void {
        const userData = this.findUserByProvider(provider);
        this.initiateOAuthSigninInternal(provider, userData);
    }

    private findUserByProvider(provider: string): { email: string; user: MockUser } | undefined {
        for (const [email, user] of this.mockUsers.entries()) {
            if (user.provider === provider) {
                return { email, user };
            }
        }
        return undefined;
    }

    private initiateOAuthSigninInternal(idp: string, userData: { email: string; user: MockUser } | undefined): void {
        console.log(`Mock: Redirecting to ${idp} OAuth`);
        // Symulacja OAuth flow - automatyczne ustawienie sesji po opóźnieniu
        setTimeout(() => {
            const email = userData?.email ?? `${idp}@example.com`;
            this.mockSession = {
                sessionId: `mock-${idp}-session`,
                expiresAt: new Date(Date.now() + 3600000).toISOString(),
                userInfo: {
                    id: `mock-${idp}-user`,
                    email: email,
                    name: `${idp.charAt(0).toUpperCase() + idp.slice(1)} User`,
                },
            };
        }, 500);
    }

    async getSessionData(): Promise<SessionDataResponse> {
        await this.simulateDelay();
        return this.mockSession ?? {
            sessionId: undefined,
            expiresAt: undefined,
            userInfo: undefined,
        };
    }

    async signout(): Promise<void> {
        await this.simulateDelay();
        this.mockSession = null;
    }

    // Helper do symulacji opóźnienia sieci
    private async simulateDelay(ms: number = 500): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Metody pomocnicze do testów (opcjonalne)
    public resetSession(): void {
        this.mockSession = null;
    }

    public addMockUser(email: string, password: string | null, provider: string | null = null): void {
        this.mockUsers.set(email, { password, provider });
    }
}

